import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { adminApi } from "../../lib/auth";
import FilterBar from "../../components/admin/FilterBar";
import { toast } from "sonner";

const downloadCsv = async (endpoint, filename, params) => {
  const qs = new URLSearchParams(params).toString();
  const token = localStorage.getItem("dvp_admin_token");
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}${endpoint}?${qs}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return toast.error("Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminVisitors() {
  const [items, setItems] = useState([]);
  const [byPage, setByPage] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.path = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await adminApi.get("/admin/visitors", { params });
      setItems(data.items);
      setByPage(data.by_page || []);
      setCount(data.count);
    } catch {
      toast.error("Failed to load visitors");
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const onExport = () => {
    const params = {};
    if (q) params.path = q;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    downloadCsv(
      "/api/admin/visitors/export",
      `visitors_${new Date().toISOString().slice(0, 10)}.csv`,
      params
    );
  };

  const maxCount = Math.max(...byPage.map((p) => p.count), 1);

  return (
    <div className="p-8 space-y-6" data-testid="admin-visitors-page">
      <div>
        <h1 className="font-display font-bold text-3xl text-white tracking-tight">
          Site Visitors
        </h1>
        <p className="text-neutral-400 text-sm mt-1">
          Anonymous traffic logs — page paths, referrers and agents.
        </p>
      </div>

      <FilterBar
        q={q}
        setQ={setQ}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        count={count}
        onRefresh={load}
        onExport={onExport}
        searchPlaceholder="Filter by path (e.g. /about)"
      />

      {byPage.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#1d4ed8] mb-4">
            Top Pages
          </div>
          <div className="space-y-2.5">
            {byPage.map((p) => (
              <div key={p.path} className="flex items-center gap-4 text-sm">
                <div className="font-mono text-neutral-300 w-48 truncate">{p.path}</div>
                <div className="flex-1 h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#1d4ed8] to-[#dc2626]"
                    style={{ width: `${(p.count / maxCount) * 100}%` }}
                  />
                </div>
                <div className="font-mono text-white w-12 text-right">{p.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] border-b border-white/10">
              <tr className="text-left text-[11px] uppercase font-mono tracking-[0.2em] text-neutral-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Path</th>
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3">Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin inline text-[#dc2626]" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-neutral-500">
                    No visits logged yet
                  </td>
                </tr>
              ) : (
                items.map((v) => (
                  <tr key={v.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-neutral-400 font-mono text-xs whitespace-nowrap">
                      {new Date(v.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-white">{v.path}</td>
                    <td className="px-4 py-3 text-neutral-400 text-xs max-w-xs truncate">
                      {v.referrer || "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-500 text-xs max-w-xs truncate">
                      {v.user_agent || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
